/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * THE SIGN OFF GATE, ONE FUNCTION FOR THE DASHBOARD AND THE ROUTE (v1_913).
 *
 * Until v1_913 the gate lived only in the browser (openSignoff, completeSignoff
 * and the Confirm button in src/MLRODashboard.jsx), and /api/case-decision
 * recorded whatever it was sent. The dashboard itself refused only a case with
 * no board, a missing rationale and an unacknowledged corrected officer; open
 * identity, document and decision condition items were shown and let through,
 * an implicit override with no named approver.
 *
 * The owner's decision (option (b)): an approval with open identity, document
 * or condition items is allowed only with a written override reason and the
 * approver's name. Both are recorded on the decision, shown in the Audit Record
 * and shown to the second signatory. The server enforces it the same way.
 *
 * Hard blocks, never overridable:
 *   an Approve on a case with no assembled board (approvalBlockedByAnalysis);
 *   no rationale;
 *   an Approve with a person corrected off the file not acknowledged by name;
 *   an Approve while a document on the case has not been read (upload fix
 *     batch, owner 15/10/2026): each must be read, accepted by the MLRO after
 *     assessing it directly, or marked superseded (notReadOpen, from the case's
 *     own document rows).
 * Overridable open items, on an Approve:
 *   identity: people in the reconciled identity set (identityParties, the set
 *     the dashboard's identityState reads) not verified, where the firm uses
 *     identity verification (features.sumsub is not false);
 *   documents: documents assessed Requires Clarification or Unsatisfactory and
 *     not accepted (materialDocuments);
 *   conditions: the reconciled blocking decision conditions (blockingOutstanding
 *     over reconcileCaseState's checks and exceptions);
 *   board: the board is provisional (report.boardStatus 'provisional', set when
 *     a step that builds it was cut off or failed), so items after the cut may
 *     be missing (cut off fix, 11/10/2026). Recorded like the other open items.
 * A Decline needs only a rationale.
 *
 * Pure, no I/O. It reads the shared stop files (case-state, board-checks,
 * analysis-complete) and never changes them.
 */
import { approvalBlockedByAnalysis, ANALYSIS_NOT_COMPLETE } from './analysis-complete.js';
import { identityRank } from './identity-method.js';
import { reconcileCaseState, identityParties } from './case-state.js';
import { materialDocuments, blockingOutstanding } from './board-checks.js';
import { samePerson as defaultSamePerson } from './person-name-match.js';
import { notReadOpen } from './doc-not-read.js';
import { individualMlroApprovalBlock, individualSignoffHardBlocks } from './individual-checks.js';
import { isIndividualCase } from './individual-case.js';
import { tierInForce, SIGNOFF_PENDING_TEXT } from './tier-in-force.js';

export const OVERRIDE_REASON_MIN = 20;
export const OVERRIDE_REASON_MAX = 2000;
export const OVERRIDE_APPROVER_MAX = 200;

// The verification map the dashboard reads (loadSumsubStatus): one row per
// person_name, a settled outcome beating a bare link, and among equals the most
// recent. Moved here unchanged so the route builds the same map from the same
// rows.
export const SUMSUB_STATUS_RANK = Object.freeze({ approved: 5, approved_with_flags: 4, requires_review: 3, rejected: 3, in_progress: 2, link_generated: 1, withdrawn: 1 });
export function verificationMapFromRows(rows, origin) {
  const map = {};
  // 29/10/2026: on a new client a Screen only row ranks below identity rows.
  const rankOf = (v) => identityRank(v, { origin });
  const timeOf = (v) => new Date((v && (v.updated_at || v.created_at)) || 0).getTime() || 0;
  (Array.isArray(rows) ? rows : []).forEach((v) => {
    if (!v || !v.person_name) return;
    const held = map[v.person_name];
    if (!held) { map[v.person_name] = v; return; }
    const better = rankOf(v) > rankOf(held) || (rankOf(v) === rankOf(held) && timeOf(v) >= timeOf(held));
    if (better) map[v.person_name] = v;
  });
  return map;
}

// People corrected off the file as not an officer (collected_data.partyRemovals
// flagged correction: true), as openSignoff lists them.
export function correctedOfficersOf(collected) {
  const pr = (collected && typeof collected === 'object' && collected.partyRemovals && typeof collected.partyRemovals === 'object') ? collected.partyRemovals : {};
  return Object.values(pr)
    .filter((e) => e && e.correction === true && e.name)
    .map((e) => ({ name: e.name, role: e.role || '', by: e.by || '', at: e.at || '', note: e.note || '' }));
}

const squash = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
const nameKey = (v) => squash(v).toLowerCase();

// A plain label for a verification status, for the open item lists.
// The open item a provisional board gives, naming the step and why (cut off fix).
export function provisionalBoardItems(report) {
  const r = (report && typeof report === 'object') ? report : {};
  if (r.boardStatus !== 'provisional') return [];
  const inc = (r.boardIncomplete && typeof r.boardIncomplete === 'object') ? r.boardIncomplete : {};
  const step = squash(inc.step) || 'a build step';
  const why = squash(inc.reason);
  return [`The board is provisional: the ${step} did not complete${why ? ' (' + why + ')' : ''}, so items after the cut may be missing.`];
}

const STATUS_LABEL = { '': 'not sent', not_sent: 'not sent', link_generated: 'link sent', pending: 'pending', in_progress: 'in progress', requires_review: 'requires review', rejected: 'rejected', withdrawn: 'withdrawn',
  // 29/10/2026: a clear Screen only result on a new client (src/shared/identity-method.js).
  screened_only: 'screened only, identity not verified: send a link or use Verify from documents' };
export const identityStatusLabel = (s) => STATUS_LABEL[String(s || '').toLowerCase()] || String(s || '').toLowerCase().replace(/_/g, ' ');

// The open items, computed from the case alone. Every input is what the board
// passes to reconcileCaseState (computeDecisionItems), so the dialog and the
// route read the same lists.
export function signoffOpenItems({
  report, collected, documents, verifications, aiReview, features,
  walletScreenings, documentTracking, documentAcknowledgments,
  contactName, contactEmail, mlroDecision, samePerson,
  formRequests, individualFormA, formRequestsPartial, origin,
} = {}) {
  const match = typeof samePerson === 'function' ? samePerson : defaultSamePerson;
  const verifMap = Array.isArray(verifications) ? verificationMapFromRows(verifications, origin) : (verifications && typeof verifications === 'object' ? verifications : {});
  const docs = Array.isArray(documents) ? documents : [];
  const feats = (features && typeof features === 'object') ? features : undefined;
  const cs = reconcileCaseState({
    report, collected_data: collected, contactName, contactEmail, origin,
    documents: docs, documentTracking: Array.isArray(documentTracking) ? documentTracking : [],
    sumsubVerifications: verifMap, aiReview: aiReview || null,
    walletScreenings: Array.isArray(walletScreenings) ? walletScreenings : [],
    mlroDecision: mlroDecision || null, documentAcknowledgments: documentAcknowledgments || undefined,
    // Individual onboarding (piece 8): the Form A rows and state, when the caller read them.
    ...(formRequests !== undefined ? { formRequests } : {}), ...(individualFormA !== undefined ? { individualFormA } : {}), ...(formRequestsPartial ? { formRequestsPartial: true } : {}),
  }, { ...(feats ? { features: feats } : {}), samePerson: match });
  const usesIdentity = ((feats || (collected && collected.firmFeatures) || {}).sumsub) !== false;
  const idp = identityParties({ report, collected_data: collected, sumsubVerifications: verifMap, contactName, contactEmail, origin }, match);
  // A NOTED FACT IS NEVER AN OPEN ITEM (owner, 04/10/2026). The board lists
  // some things as facts to state in the rationale, not conditions on the
  // decision (cs.noted: items outstanding with the client, procedural checks
  // that complete after approval). Those are shown in the dialog's noted box
  // and never ask for an override; only what is not noted can. v1_913b: an
  // unverified identity check and a document needing clarification are not
  // noted (cs.openForOverride), so they count here as open.
  const noted = (Array.isArray(cs.noted) ? cs.noted : []).map((x) => String(x));
  const notedMentions = (name) => !!name && noted.some((n) => n.includes(String(name)));
  const idCheck = (Array.isArray(cs.procedural) ? cs.procedural : []).find((c) => c && c.key === 'sumsub');
  const identityNoted = !!idCheck && !idCheck.done && noted.some((n) => n.startsWith(String(idCheck.q)));
  const identityResolved = !!idCheck && idCheck.done;
  return {
    identity: usesIdentity && !identityNoted && !identityResolved
      ? idp.outstanding.filter((p) => !notedMentions(p.name)).map((p) => ({ name: p.name, role: p.role || '', status: identityStatusLabel(p.status) })) : [],
    documents: materialDocuments(docs).filter((d) => !notedMentions(d.file_name || d.doc_type)).map((d) => ({ name: d.file_name || d.doc_type || 'a document', assessment: d.assessment })),
    // A document not read is a hard block (unread_documents), never an overridable condition.
    conditions: blockingOutstanding(cs.checks, cs.exceptions.filter((e) => !(e && e.key === 'notread'))).map((x) => String(x)).filter((c) => !noted.includes(c)),
    board: provisionalBoardItems(report),
    noted,
  };
}

// Individual onboarding (review finding B1): what no override clears on an
// individual's approval, from the same inputs the open items read. [] for a
// company case.
export function individualHardBlocks({
  report, collected, documents, verifications, aiReview, features,
  walletScreenings, documentTracking, documentAcknowledgments,
  contactName, contactEmail, mlroDecision, samePerson,
  formRequests, individualFormA, formRequestsPartial, origin,
} = {}) {
  if (!isIndividualCase(collected)) return [];
  const match = typeof samePerson === 'function' ? samePerson : defaultSamePerson;
  const verifMap = Array.isArray(verifications) ? verificationMapFromRows(verifications, origin) : (verifications && typeof verifications === 'object' ? verifications : {});
  const feats = (features && typeof features === 'object') ? features : undefined;
  const cs = reconcileCaseState({
    report, collected_data: collected, contactName, contactEmail, origin,
    documents: Array.isArray(documents) ? documents : [], documentTracking: Array.isArray(documentTracking) ? documentTracking : [],
    sumsubVerifications: verifMap, aiReview: aiReview || null,
    walletScreenings: Array.isArray(walletScreenings) ? walletScreenings : [],
    mlroDecision: mlroDecision || null, documentAcknowledgments: documentAcknowledgments || undefined,
    ...(formRequests !== undefined ? { formRequests } : {}), ...(individualFormA !== undefined ? { individualFormA } : {}), ...(formRequestsPartial ? { formRequestsPartial: true } : {}),
  }, { ...(feats ? { features: feats } : {}), samePerson: match });
  const idp = identityParties({ report, collected_data: collected, sumsubVerifications: verifMap, contactName, contactEmail, origin }, match);
  const cp = (report && report.clientPerson) || {};
  return individualSignoffHardBlocks(cs, idp.outstanding, cp.fullName || (report && report.clientName) || contactName, match);
}

export const openItemCount = (oi) => ((oi && oi.identity) || []).length + ((oi && oi.documents) || []).length + ((oi && oi.conditions) || []).length + ((oi && oi.board) || []).length;

// The override as checked: the reason trimmed and the approver's name with its
// spaces squashed, each capped, so what is stored is exactly what was checked.
export function normaliseOverride(override) {
  const o = (override && typeof override === 'object') ? override : {};
  return {
    reason: String(o.reason == null ? '' : o.reason).trim().slice(0, OVERRIDE_REASON_MAX),
    approver: squash(o.approver).slice(0, OVERRIDE_APPROVER_MAX),
  };
}
export function overrideValid(override) {
  const o = normaliseOverride(override);
  return o.reason.length >= OVERRIDE_REASON_MIN && o.approver.length > 0;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
export function openItemsSummary(oi) {
  const parts = [];
  if ((oi.identity || []).length) {
    // 29/10/2026: a person with only a Screen only result is named, with what they still need.
    const screened = oi.identity.filter((p) => p && p.status === STATUS_LABEL.screened_only).map((p) => p.name);
    parts.push(plural(oi.identity.length, 'person not verified', 'people not verified') + (screened.length ? ` (${screened.join(', ')} screened only, identity not verified: send a link or use Verify from documents before sign off)` : ''));
  }
  if ((oi.documents || []).length) parts.push(plural(oi.documents.length, 'document not satisfactory', 'documents not satisfactory'));
  if ((oi.conditions || []).length) parts.push(plural(oi.conditions.length, 'decision condition open', 'decision conditions open'));
  if ((oi.board || []).length) parts.push('the board is provisional');
  return parts.join(', ');
}

/**
 * @param input {
 *   report, collected, documents, verifications (rows or the name keyed map),
 *   aiReview, decision ('Approve' | 'Decline'), rationale,
 *   override { reason, approver }, acknowledgements { correctedOfficers: [names] },
 *   features, walletScreenings, documentTracking, documentAcknowledgments,
 *   contactName, contactEmail, mlroDecision, samePerson
 * }
 * @returns { allowed, hardBlocks[{ key, text }], openItems { identity[], documents[], conditions[] },
 *            overrideRequired, overrideValid, override, correctedOfficers[], reasons[] }
 */
export function signoffGate(input = {}) {
  const decision = String(input.decision || '');
  const rationale = String(input.rationale == null ? '' : input.rationale).trim();
  const hardBlocks = [];
  const empty = { identity: [], documents: [], conditions: [], board: [] };
  const corrected = correctedOfficersOf(input.collected);

  // HOTFIX 07/10/2026: Request Info is a decision too. v1_913 refused every
  // decision but Approve and Decline, so on production the Confirm & Request
  // Info button could never be pressed (case CID-2026-5309, Bloxcross, Inc.).
  // A request for information is sent because items are open, so it needs only
  // the message to the client: no open item check, no override, no checklist.
  if (decision === 'Request Info') {
    if (!rationale) hardBlocks.push({ key: 'rationale', text: 'No message has been entered. Say what information is needed and why.' });
    return { allowed: hardBlocks.length === 0, hardBlocks, openItems: empty, overrideRequired: false, overrideValid: false, override: null, correctedOfficers: corrected, reasons: hardBlocks.map((h) => h.text) };
  }
  if (decision !== 'Approve' && decision !== 'Decline') {
    hardBlocks.push({ key: 'decision', text: 'The decision must be Approve, Decline or Request Info.' });
  }
  if (!rationale) {
    hardBlocks.push({ key: 'rationale', text: decision === 'Decline' ? 'No decline rationale has been entered. Enter the reason for the decision.' : 'No approval rationale has been entered. Enter the basis for the decision.' });
  }
  // MODEL TIER PENDING (owner, 29/10/2026): no approval while the case shows
  // "Assessment pending". With no tier in force (the engine has not completed,
  // and the model's proposal never counts as one) there is no tier to approve,
  // so an Approve is refused with the reason, never overridable. A Decline is
  // allowed on every pending case, with or without a board (owner, 29/10/2026):
  // api/case-decision.js records that it was declined before an engine
  // assessment existed. A request for information (above) is not refused.
  // Only where an assembled board exists: with no board the no board block
  // below says it alone.
  if (decision === 'Approve' && !approvalBlockedByAnalysis({ report: input.report }).blocked && tierInForce(input.report || {}) === null) {
    hardBlocks.push({ key: 'assessment_pending', text: SIGNOFF_PENDING_TEXT });
  }
  if (decision !== 'Approve') {
    return { allowed: hardBlocks.length === 0, hardBlocks, openItems: empty, overrideRequired: false, overrideValid: false, override: null, correctedOfficers: corrected, reasons: hardBlocks.map((h) => h.text) };
  }

  if (approvalBlockedByAnalysis({ report: input.report }).blocked) {
    hardBlocks.push({ key: 'no_board', text: ANALYSIS_NOT_COMPLETE + ': this case has no assembled board, so it cannot be approved. Reprocess to finish the analysis first.' });
  }
  const acked = new Set(((input.acknowledgements && Array.isArray(input.acknowledgements.correctedOfficers)) ? input.acknowledgements.correctedOfficers : []).map(nameKey).filter(Boolean));
  const notAcked = corrected.filter((o) => !acked.has(nameKey(o.name)));
  if (notAcked.length) {
    hardBlocks.push({ key: 'corrected_officers', text: `A person corrected off the file as not an officer has not been acknowledged by name (${notAcked.map((o) => o.name).join(', ')}). Confirm each removal in the checklist.` });
  }

  const unread = unreadDocumentsBlock(input.documents);
  if (unread) hardBlocks.push(unread);
  // Individual onboarding (piece 8): a high risk individual is approved by the MLRO, by name.
  const mlroBlock = individualMlroApprovalBlock({ report: input.report, collected: input.collected, decision, mlroApproval: input.mlroApproval, policyOfficers: input.policyOfficers, samePerson: input.samePerson, sessionTier: input.sessionTier });
  if (mlroBlock) hardBlocks.push(mlroBlock);
  if (!approvalBlockedByAnalysis({ report: input.report }).blocked) hardBlocks.push(...individualHardBlocks(input));

  // With no board there is nothing to reconcile; the no board block stands alone.
  const openItems = hardBlocks.some((h) => h.key === 'no_board') ? empty : signoffOpenItems(input);
  const overrideRequired = openItemCount(openItems) > 0;
  const ov = normaliseOverride(input.override);
  const ovOk = overrideValid(ov);
  const reasons = hardBlocks.map((h) => h.text);
  if (overrideRequired && !ovOk) {
    const missing = [
      ov.reason.length < OVERRIDE_REASON_MIN ? `a written override reason of at least ${OVERRIDE_REASON_MIN} characters` : null,
      !ov.approver ? 'the name of the approver' : null,
    ].filter(Boolean).join(' and ');
    reasons.push(`This approval has open items (${openItemsSummary(openItems)}). An approval with open items needs ${missing}.`);
  }
  const allowed = hardBlocks.length === 0 && (!overrideRequired || ovOk);
  return {
    allowed, hardBlocks, openItems, overrideRequired, overrideValid: ovOk,
    override: overrideRequired && ovOk ? ov : null,
    correctedOfficers: corrected, reasons,
  };
}

// The hard block for documents not read (owner 15/10/2026): never overridable.
// Shared by sign off and by the acceptance of a migrated client.
export function unreadDocumentsBlock(documents) {
  const open = notReadOpen(documents);
  if (!open.length) return null;
  const n = open.length;
  return { key: 'unread_documents', items: open, text: `${n === 1 ? '1 document has' : n + ' documents have'} not been read (${open.map((d) => d.name).join(', ')}). Each must be read, accepted by the MLRO after assessing it directly, or marked superseded before the file can be approved.` };
}

// The override as the decision record stores it: what was checked, who recorded
// it, when, and the open items at that moment.
export function overrideRecord({ override, openItems, recordedBy, recordedByEmail, recordedAt, checkedBy }) {
  const ov = normaliseOverride(override);
  return {
    reason: ov.reason,
    approver: ov.approver,
    recordedBy: squash(recordedBy) || 'MLRO',
    ...(recordedByEmail ? { recordedByEmail: squash(recordedByEmail) } : {}),
    recordedAt: recordedAt || new Date().toISOString(),
    openItems: {
      identity: ((openItems && openItems.identity) || []).map((p) => ({ name: p.name, role: p.role || '', status: p.status || '' })),
      documents: ((openItems && openItems.documents) || []).map((d) => ({ name: d.name, assessment: d.assessment || '' })),
      conditions: ((openItems && openItems.conditions) || []).map((c) => String(c)),
      board: ((openItems && openItems.board) || []).map((c) => String(c)),
    },
    // The noted facts at that moment, recorded beside the override but never
    // part of it (a noted fact is not an open item).
    noted: ((openItems && openItems.noted) || []).map((n) => String(n)),
    ...(checkedBy ? { checkedBy } : {}),
  };
}

// The open items as plain lines, for the Audit Record, the second signatory and
// case notes (one shape, one wording).
export function overrideItemLines(rec) {
  const oi = (rec && rec.openItems) || {};
  return [
    ...(oi.identity || []).map((p) => `Identity not verified: ${p.name}${p.role ? ' (' + p.role + ')' : ''}, ${p.status || 'not sent'}`),
    ...(oi.documents || []).map((d) => `Document not satisfactory: ${d.name}, ${d.assessment || 'not assessed'}`),
    ...(oi.conditions || []).map((c) => `Decision condition open: ${c}`),
    ...(oi.board || []).map((c) => `Board provisional: ${c}`),
  ];
}

// Every line of override copy an officer, an auditor or the second signatory
// reads, in one place: the sign off dialog, the Audit Record and the second
// signatory screen read these, and the harness checks them for dashes.
export const OVERRIDE_COPY = Object.freeze({
  dialogTitle: 'Open items on this approval',
  dialogIntro: 'You can approve with these items open only with a written override reason and the name of the person approving the override. Both are recorded on the decision, shown in the Audit Record and shown to the second signatory.',
  identityHead: 'Identity not verified',
  documentsHead: 'Documents not satisfactory',
  conditionsHead: 'Decision conditions open',
  boardHead: 'Board provisional',
  reasonLabel: 'Override reason',
  reasonPlaceholder: `Why this file can be approved with these items open (at least ${OVERRIDE_REASON_MIN} characters)`,
  approverLabel: 'Approver name',
  approverPlaceholder: 'Name of the person approving the override',
  approverPrefilled: 'Prefilled with the override approver named in the firm\'s policy. Change it if someone else approves.',
  reasonCount: (n) => (n >= OVERRIDE_REASON_MIN ? `${n} characters` : `${n} of ${OVERRIDE_REASON_MIN} characters`),
  confirmHint: `Enter an override reason of at least ${OVERRIDE_REASON_MIN} characters and the approver's name to continue.`,
  notedFooter: 'These are facts to state in your rationale, not conditions on the decision.',
  auditHeading: 'Override of open items',
  auditNone: 'No override recorded with this decision.',
  reviewTitle: 'Approved with open items, by override',
  reviewIntro: 'The MLRO approved this file while the items below were open. The override reason and the approver were recorded with the decision, and the server checked the open items when it was recorded.',
  rowReason: 'Override reason',
  rowApprover: 'Override approver (name entered)',
  rowRecordedBy: 'Recorded by (signed in user)',
  rowRecordedAt: 'Recorded at',
  rowItems: 'Open when approved',
});
